// src/components/ITTimeline/ITTimeline.jsx

import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FiZap,
  FiTrendingUp,
  FiBarChart2,
  FiLayers,
  FiShield,
  FiCheckCircle,
} from "react-icons/fi";

import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";
import { useSiteContent } from "../../content/SiteContentContext";

// Vaiheet ovat staattisia, joten niitä ei tarvitse rakentaa useMemo-hookilla.
const steps = [
  { id: "speed", icon: FiZap, align: "left", aos: "fade-right" },
  { id: "seo", icon: FiTrendingUp, align: "right", aos: "fade-left" },
  { id: "tracking", icon: FiBarChart2, align: "left", aos: "fade-right" },
  { id: "architecture", icon: FiLayers, align: "right", aos: "fade-left" },
  { id: "reliability", icon: FiShield, align: "left", aos: "fade-right" },
  { id: "delivery", icon: FiCheckCircle, align: "right", aos: "fade-left" },
];

const ITTimeline = ({ lang: langProp } = {}) => {
  useSiteContent();
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const stepRefs = useRef([]);

  // Progress mitataan nyt pikseleinä suhteessa itse timelineen.
  // Näin jana ja solmut pysyvät täsmälleen synkassa.
  const [progressPx, setProgressPx] = useState(0);
  const [activeStepCount, setActiveStepCount] = useState(1);

  // Home välittää normaalisti kielen propina.
  // Jos komponenttia käytetään ilman sitä, päätellään kieli URL:sta.
  const pathname =
    typeof window !== "undefined"
      ? window.location.pathname
      : "/";

  const lang =
    typeof langProp === "string" && langProp
      ? normalizeLang(langProp)
      : getLangFromPathname(pathname);

  // Käytetään yhteistä i18n.js:n käännösfunktiota.
  const t = (key, vars) => translate(key, lang, vars);

  useEffect(() => {
    AOS.init({
      duration: 550,
      easing: "ease-out-cubic",
      once: true,
    });
  }, []);

  // Jana seuraa viewportissa olevaa trigger-kohtaa.
  // Solmu syttyy vasta täsmälleen silloin, kun jana saavuttaa sen.
  useEffect(() => {
    const onScroll = () => {
      if (!timelineRef.current) return;

      const timelineRect =
        timelineRef.current.getBoundingClientRect();

      const viewH = window.innerHeight || 1;

      // Kohta, jossa jana "etenee" käyttäjän mukana.
      // 58 % viewportin korkeudesta tuntuu luonnolliselta:
      // vaihe syttyy hieman ruudun keskikohdan alapuolella.
      const triggerY = viewH * 0.58;

      const nodePositions = stepRefs.current
        .map((element) => {
          if (!element) return null;

          const rect = element.getBoundingClientRect();

          return (
            rect.top +
            rect.height / 2 -
            timelineRect.top
          );
        })
        .filter((value) => typeof value === "number");

      if (!nodePositions.length) return;

      const firstNodeY = nodePositions[0];

      // Ensimmäinen solmu saa olla jo valmiiksi syttynyt,
      // joten myös jana alkaa vähintään ensimmäiseen solmuun asti.
      const rawProgress =
        triggerY - timelineRect.top;

      const clampedProgress = Math.min(
        Math.max(rawProgress, firstNodeY),
        timelineRect.height
      );

      setProgressPx(clampedProgress);

      // Solmut aktivoituvat suoraan janan korkeuden perusteella.
      // Tämän ansiosta solmu ei voi syttyä ennen janaa eikä jäädä sen jälkeen.
      let passedSteps = 0;

      nodePositions.forEach((nodeY) => {
        if (nodeY <= clampedProgress + 1) {
          passedSteps += 1;
        }
      });

      setActiveStepCount(
        Math.max(1, passedSteps)
      );
    };

    onScroll();

    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      id="ITTimeline"
      ref={sectionRef}
      className="mx-auto w-full scroll-mt-24 px-2 pb-24 sm:px-4 md:px-6 lg:px-12 xl:px-16"
      aria-label={t("timeline.ariaLabel")}
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="relative overflow-hidden rounded-[2rem] border border-black/10 bg-white/70 px-6 py-8 shadow-[0_20px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_20px_80px_rgba(0,0,0,0.25)] md:px-10 md:py-10">
          {/* Hienovaraiset taustahehkut */}
          <div
            className="pointer-events-none absolute -left-20 -top-24 h-72 w-72 rounded-full bg-lime-400/20 opacity-40 blur-3xl"
            aria-hidden="true"
          />

          <div
            className="pointer-events-none absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-sky-400/20 opacity-35 blur-3xl"
            aria-hidden="true"
          />

          {/* Otsikko */}
          <div className="relative z-10 mx-auto mb-12 max-w-3xl text-center">
            <h2
              className="text-3xl font-bold tracking-tight text-black dark:text-white md:text-4xl"
              data-aos="fade-down"
            >
              {t("timeline.title")}
            </h2>

            <p
              className="mx-auto mt-4 max-w-2xl text-base font-light leading-relaxed text-black/75 dark:text-white/75 md:text-lg"
              data-aos="fade-up"
            >
              {t("timeline.subtitle")}
            </p>
          </div>

          {/* Timeline */}
          <div
            ref={timelineRef}
            className="relative z-10"
          >
            {/* Keskiviiva */}
            <div
              className="absolute bottom-0 left-1/2 top-0 hidden w-[2px] -translate-x-1/2 rounded-full bg-black/10 dark:bg-white/10 md:block"
              aria-hidden="true"
            />

            {/* Scroll-progress:
                korkeus on nyt pikseleinä eikä prosenttina,
                jotta pää osuu täsmälleen solmujen kohdalle.
            */}
            <div
              className="absolute left-1/2 top-0 hidden w-[2px] -translate-x-1/2 rounded-full bg-gradient-to-b from-lime-400 via-sky-400 to-indigo-400 shadow-[0_0_24px_rgba(132,204,22,0.45)] md:block"
              style={{
                height: `${progressPx}px`,
              }}
              aria-hidden="true"
            />

            <div className="space-y-5 md:space-y-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isLeft = step.align === "left";
                const isActive =
                  index < activeStepCount;

                return (
                  <div
                    key={step.id}
                    ref={(element) => {
                      stepRefs.current[index] = element;
                    }}
                    className="relative flex flex-col items-stretch md:flex-row md:items-center"
                  >
                    {!isLeft && (
                      <div className="hidden w-1/2 md:block" />
                    )}

                    <div
                      className={`w-full md:w-1/2 ${
                        isLeft
                          ? "md:pr-10"
                          : "md:pl-10"
                      }`}
                      data-aos={step.aos}
                    >
                      <article className="group relative overflow-hidden rounded-2xl border border-black/10 bg-white/80 px-5 py-5 transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5 sm:px-6 sm:py-6">
                        <div
                          className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-lime-400/20 opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-30"
                          aria-hidden="true"
                        />

                        <div className="relative flex items-start gap-4">
                          <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-black/10 bg-black/[0.04] dark:border-white/10 dark:bg-white/5"
                            aria-hidden="true"
                          >
                            <Icon
                              className="text-black/80 dark:text-white/80"
                              size={22}
                            />
                          </div>

                          <div className="flex-1 text-left">
                            <div className="text-xs font-bold uppercase tracking-[0.18em] text-black/45 dark:text-white/45">
                              {t(
                                `timeline.steps.${step.id}.tag`
                              )}
                            </div>

                            <h3 className="mt-1 text-xl font-bold text-black dark:text-white sm:text-2xl">
                              {t(
                                `timeline.steps.${step.id}.title`
                              )}
                            </h3>

                            <p className="mt-2 text-sm leading-relaxed text-black/70 dark:text-white/70 sm:text-base">
                              {t(
                                `timeline.steps.${step.id}.desc`
                              )}
                            </p>

                            <ul className="mt-4 space-y-2">
                              {[0, 1, 2].map((i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-sm leading-relaxed text-black/70 dark:text-white/70"
                                >
                                  <span
                                    className="mt-[7px] inline-block h-2 w-2 shrink-0 rounded-full bg-lime-400/80 shadow-[0_0_15px_rgba(132,204,22,0.35)]"
                                    aria-hidden="true"
                                  />

                                  <span>
                                    {t(
                                      `timeline.steps.${step.id}.bullets.${i}`
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </article>
                    </div>

                    {/* Keskisolmu:
                        aktivoituu täsmälleen silloin,
                        kun progress-jana saavuttaa tämän kohdan.
                    */}
                    <div
                      className="absolute left-1/2 hidden -translate-x-1/2 items-center justify-center md:flex"
                      aria-hidden="true"
                    >
                      <div
                        className={[
                          "h-4 w-4 rounded-full border transition-all duration-300",
                          isActive
                            ? "scale-110 border-lime-300 bg-lime-400 shadow-[0_0_8px_rgba(163,230,53,0.95),0_0_24px_rgba(132,204,22,0.8),0_0_42px_rgba(132,204,22,0.45)]"
                            : "scale-100 border-black/20 bg-gray-950 shadow-none dark:border-white/15 dark:bg-black",
                        ].join(" ")}
                      />
                    </div>

                    {isLeft && (
                      <div className="hidden w-1/2 md:block" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Alateksti */}
          <div className="relative z-10 mx-auto mt-10 max-w-3xl border-t border-black/10 pt-8 text-center dark:border-white/10">
            <p className="text-sm leading-relaxed text-black/70 dark:text-white/70 sm:text-base">
              {t("timeline.footer")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ITTimeline;
